import { consoleLog } from "./consoleLog/consoleLog";

/**
 * RAW (unencrypted) localStorage marker key holding the build version that last
 * completed boot cleanup. Deliberately NOT written through the encrypted
 * setItemInLocalStorage/getItemFromLocalStorage helpers in hooks/localStorage.tsx:
 * ENCRYPTION_KEY is environment-dependent and updated asynchronously by
 * getFEConfig, so an encrypted marker could fail to decrypt at boot and
 * re-trigger cleanup on every single load. A raw plain string is deterministic
 * and dependency-free.
 */
export const BUILD_VERSION_STORAGE_KEY = "_zenvoya_build_version";

const EXPIRED = "expires=Thu, 01 Jan 1970 00:00:00 GMT";

/**
 * localStorage keys that MUST survive the stale-build wipe (product decision:
 * clear all stale state but keep the user logged in):
 *   - "_zenvoya_access_JWT" + "user_info" — the auth bearer token and the
 *     UserInfo object (id/email/loggedIn). Preserving both keeps the session
 *     alive across deploys, so no login flow breaks.
 *   - BUILD_VERSION_STORAGE_KEY (our marker) + "clearStorage" (the counter used
 *     by checkAndClearStorageIfNeeded in hooks/localStorage.tsx). Both are
 *     MANDATORY: wiping either lets the two boot-time cleaners clobber each
 *     other's marker on every load, so the wipe would run on EVERY load instead
 *     of once per deploy.
 * The wipe is an ALLOWLIST-REMOVE against this set — never localStorage.clear().
 */
const PRESERVED_LOCAL_STORAGE_KEYS = new Set<string>([
  "_zenvoya_access_JWT",
  "user_info",
  BUILD_VERSION_STORAGE_KEY,
  "clearStorage",
]);

/**
 * One-shot stale-build cleanup. Takes the build version AND the master on/off flag
 * (APP_CONFIG.ACTIVATE_BUILD_CLEANUP) as PARAMETERS — both from backend appConfig,
 * resolved by the caller.
 * Driven from a SINGLE source: useAppInitialization calls it with the admin-
 * controlled APP_CONFIG.BUILD_VERSION row value once appConfig loads (which needs
 * a valid JWT — guest or logged-in — so it covers everyone). Bumping that appConfig
 * row triggers a one-time cache-bust for all clients with NO code deploy. There is
 * deliberately NO bundle-baked boot-time caller (single source of truth = no risk
 * of two callers clobbering the shared marker). Auth is preserved by the wipe, so
 * running after appConfig loads never logs the user out. This is distinct from the
 * backend-driven APP_VERSION in constant/environment.ts.
 *
 * Compares the given buildVersion against the raw localStorage marker:
 *   - match            -> early return, zero work;
 *   - mismatch or null -> expire host-scoped cookies, wipe stale localStorage
 *                         (all keys except PRESERVED_LOCAL_STORAGE_KEYS), delete
 *                         all CacheStorage entries, unregister leftover service
 *                         workers, write the new marker. Reload ONLY if an old
 *                         service worker was actually registered AND this is a
 *                         returning visitor (stored !== null) AND the marker
 *                         verifiably persisted (reload-loop guard).
 *
 * Invariants:
 *   - wipes localStorage by ALLOWLIST-REMOVE, never localStorage.clear(): the
 *     auth keys (_zenvoya_access_JWT, user_info — user stays logged in) and the
 *     internal markers (_zenvoya_build_version, clearStorage) always survive;
 *   - never throws / never blocks first render (every step is caught);
 *   - no service worker is registered by this app today, so steps 3/4/6 are
 *     insurance against historical deploys. If a real SW is ever added, note
 *     that every version bump will unregister it once and reload returning users.
 */
export async function cleanupStaleBuild(buildVersion: string, active: boolean): Promise<void> {
  try {
    // Master kill-switch — inert when the backend appConfig flag
    // APP_CONFIG.ACTIVATE_BUILD_CLEANUP is off (resolved + passed in by the caller).
    if (!active) return;
    // No version to compare against (e.g. the appConfig row is absent) → do nothing.
    if (!buildVersion) return;
    const stored = localStorage.getItem(BUILD_VERSION_STORAGE_KEY);
    if (stored === buildVersion) return; // fast path — one raw getItem per load

    consoleLog(
      `[staleBuildCleanup] stored "${stored}" !== "${buildVersion}" — running one-time cleanup`
    );

    // 1) Expire cookies — host-scoped (path=/) variants only. Domain cookies
    //    (domain=.zenvoya.ai) are intentionally untouched so the
    //    `${environment}userData` cross-subdomain SSO cookie and the
    //    _ga/_fbp/_fbc/_fprom_tid attribution cookies survive. The explicit
    //    userData skip is belt-and-braces per the product decision. HttpOnly
    //    cookies are invisible to document.cookie and silently skipped.
    try {
      const cookies = document.cookie ? document.cookie.split(";") : [];
      for (const cookie of cookies) {
        const name = cookie.split("=")[0].trim();
        if (!name || name.endsWith("userData")) continue; // preserve `${environment}userData` SSO cookie
        document.cookie = `${name}=; ${EXPIRED}; path=/`;
      }
    } catch (error) {
      consoleLog(["[staleBuildCleanup] cookie expiry failed (non-fatal)", error]);
    }

    // 2) Wipe stale localStorage — every key EXCEPT PRESERVED_LOCAL_STORAGE_KEYS
    //    (auth + internal markers). ALLOWLIST-REMOVE, NOT localStorage.clear():
    //    clear() would log the user out (wipes the auth keys) and wipe the
    //    clearStorage counter (making checkAndClearStorageIfNeeded clobber our
    //    marker every load). Object.keys() is a snapshot, so removeItem during
    //    the loop is safe. Values are never read/decrypted — matched by key name
    //    only, so encrypted auth entries are preserved intact.
    try {
      const keys = Object.keys(localStorage);
      for (const key of keys) {
        if (!PRESERVED_LOCAL_STORAGE_KEYS.has(key)) localStorage.removeItem(key);
      }
    } catch (error) {
      consoleLog(["[staleBuildCleanup] localStorage wipe failed (non-fatal)", error]);
    }

    // 3) Delete every CacheStorage entry (stale precached assets from any old
    //    service worker). `caches` is undefined on insecure origins — guarded.
    try {
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((key) => caches.delete(key)));
      }
    } catch (error) {
      consoleLog(["[staleBuildCleanup] CacheStorage cleanup failed (non-fatal)", error]);
    }

    // 4) Unregister any leftover service workers.
    let hadServiceWorker = false;
    try {
      if ("serviceWorker" in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        hadServiceWorker = registrations.length > 0;
        await Promise.all(registrations.map((registration) => registration.unregister()));
      }
    } catch (error) {
      consoleLog(["[staleBuildCleanup] service worker unregister failed (non-fatal)", error]);
    }

    // 5) Persist the new marker BEFORE any reload — and regardless of whether
    //    steps 1-4 partially failed — so the next load hits the early return.
    try {
      localStorage.setItem(BUILD_VERSION_STORAGE_KEY, buildVersion);
    } catch (error) {
      consoleLog(["[staleBuildCleanup] marker write failed (non-fatal)", error]);
    }

    // 6) Reload once, and only when it can matter: a stale SW could actually
    //    have served this page (hadServiceWorker) AND this is not a brand-new
    //    visitor (stored !== null) AND the marker verifiably persisted — the
    //    re-read guarantees the next load early-returns, making a reload loop
    //    impossible even where localStorage writes are silently dropped
    //    (e.g. iOS Safari private mode).
    if (
      stored !== null &&
      hadServiceWorker &&
      localStorage.getItem(BUILD_VERSION_STORAGE_KEY) === buildVersion
    ) {
      consoleLog("[staleBuildCleanup] stale service worker found — reloading once for fresh assets");
      window.location.reload();
    }
  } catch (error) {
    consoleLog(["[staleBuildCleanup] cleanup failed (non-fatal)", error]); // never block render
  }
}
